import type { ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { execute } from '@/graphql/execute';
import { ANALYSIS_RUN_FILES_KEY } from '@/lib/analyses/runFilesKey';
import { ANALYSIS_DIALOG_OPTIONS, ANALYSIS_SHELL_CLASS } from '@/lib/analysisDialog';
import { landxmlMergeWarningTexts, type LandxmlMergeStatsWarnings } from '@/lib/analyses/landxmlMergeWarnings';
import { useDialog } from '@/providers/DialogProvider';
import { executeLandxmlMergeCleanupMutation, landxmlMergeCleanupOperationQuery,
  previewLandxmlMergeCleanupQuery } from '@/queries/landxmlMerge.query';

type MergeLayer = { id: string; type?: string | null; root?: string | null; metadata?: unknown };
const reasonKeys = {
  notMergeResult: 'landxmlMerge.cleanup.reasons.notMergeResult',
  mergedOutputMissing: 'landxmlMerge.cleanup.reasons.mergedOutputMissing',
  unsupportedContent: 'landxmlMerge.cleanup.reasons.unsupportedContent',
  sourceNotUploaded: 'landxmlMerge.cleanup.reasons.sourceNotUploaded',
  containsOtherData: 'landxmlMerge.cleanup.reasons.containsOtherData',
  sourceChanged: 'landxmlMerge.cleanup.reasons.sourceChanged',
  sourceProcessing: 'landxmlMerge.cleanup.reasons.sourceProcessing',
  sourceInUse: 'landxmlMerge.cleanup.reasons.sourceInUse',
  alreadyCleaned: 'landxmlMerge.cleanup.reasons.alreadyCleaned',
  previewUnavailable: 'landxmlMerge.cleanup.reasons.previewUnavailable',
} as const;
export function isMergedLandxmlResult(layer: MergeLayer): boolean {
  const meta = layer.metadata as Record<string, unknown> | null;
  return layer.type === 'MODEL' && !!layer.root && meta?.type === 'LANDXML' &&
    typeof meta.sourceProcessJobId === 'string' && typeof meta.batchId === 'string' &&
    Array.isArray(meta.sourceLayerIds) && meta.sourceLayerIds.length >= 2 &&
    typeof meta.landxmlMergeStats === 'object' && meta.landxmlMergeStats !== null;
}

export function hasLandxmlMergeCleanupOperation(layer: MergeLayer): boolean {
  const meta = layer.metadata as Record<string, unknown> | null;
  return typeof meta?.sourceCleanupOperationId === 'string';
}

export function openLandxmlMergeCleanupDialog(
  openDialog: (content: ReactElement, className?: string, options?: typeof ANALYSIS_DIALOG_OPTIONS) => void,
  layer: MergeLayer, projectId: string,
) {
  openDialog(<LandxmlMergeCleanupDialog layer={layer} projectId={projectId} />,
    ANALYSIS_SHELL_CLASS, ANALYSIS_DIALOG_OPTIONS);
}

export function LandxmlMergeCleanupDialog({ layer, projectId }: { layer: MergeLayer; projectId: string }) {
  const { t } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: ['landxmlMergeCleanupPreview', layer.id],
    queryFn: () => execute(previewLandxmlMergeCleanupQuery, { resultLayerId: layer.id }),
    retry: false,
  });
  const impact = preview.data?.previewLandxmlMergeCleanup;
  const mutation = useMutation({
    mutationFn: async () => {
      if (!impact?.eligible || !impact.fingerprint) throw new Error(t('landxmlMerge.cleanup.previewUnavailable'));
      return execute(executeLandxmlMergeCleanupMutation, { resultLayerId: layer.id, fingerprint: impact.fingerprint });
    },
    onSuccess: () => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['layers', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['analysisRuns', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['projectJobs', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['batches'] }),
        queryClient.invalidateQueries({ queryKey: ['deletionOperations', projectId] }),
        queryClient.invalidateQueries({ queryKey: [ANALYSIS_RUN_FILES_KEY] }),
        preview.refetch(),
      ]);
    },
    onError: () => { void preview.refetch(); },
  });
  const operationId = mutation.data?.executeLandxmlMergeCleanup.id ?? impact?.operationId;
  const operation = useQuery({
    queryKey: ['landxmlMergeCleanupOperation', projectId, operationId],
    queryFn: () => execute(landxmlMergeCleanupOperationQuery, { projectId, id: operationId! }),
    enabled: !!operationId,
    refetchInterval: (query) => ['PENDING', 'RUNNING', 'RETRYING'].includes(
      query.state.data?.deletionOperation.status ?? '',
    ) ? 5000 : false,
  });
  const stats = (layer.metadata as { landxmlMergeStats?: LandxmlMergeStatsWarnings } | null)
    ?.landxmlMergeStats;
  const warnings = landxmlMergeWarningTexts(stats, t);
  const bytes = impact?.storageBytes == null ? null : Number(impact.storageBytes);
  return <>
    <DialogHeader>
      <DialogTitle>{t('landxmlMerge.cleanup.title')}</DialogTitle>
      <DialogDescription>{t('landxmlMerge.cleanup.description')}</DialogDescription>
    </DialogHeader>
    <div className='min-h-0 flex-1 space-y-3 overflow-y-auto py-2 text-sm'>
      {preview.isPending && <p role='status'>{t('landxmlMerge.cleanup.loading')}</p>}
      {preview.error && <p role='alert' className='text-destructive'>{preview.error.message}</p>}
      {impact && <>
        <p>{t('landxmlMerge.cleanup.sources')}</p>
        <ul className='list-disc pl-5'>{impact.sourceLayerNames.map((name, index) => <li key={`${index}-${name}`}>{name}</li>)}</ul>
        {impact.eligible && !operationId && <>
          {impact.uploadBatches.length > 0 && <>
            <p>{t('landxmlMerge.cleanup.batches')}</p>
            <ul className='list-disc pl-5'>{impact.uploadBatches.map((batch) => <li key={batch.id}>
              {batch.label !== batch.id ? `${batch.label} (${batch.id})` : batch.id}
            </li>)}</ul>
          </>}
          <p>{t('landxmlMerge.cleanup.impact', { batches: impact.uploadBatchCount,
            attachments: impact.attachmentCount })}</p>
          {bytes !== null && <p>{t('landxmlMerge.cleanup.storage', { bytes: bytes.toLocaleString() })}</p>}
        </>}
        <p>{t('landxmlMerge.cleanup.resultKept')}</p>
        {warnings.length > 0 && <div role='note' className='rounded-md border border-amber-400 p-2'>
          <p>{t('landxmlMerge.cleanup.warnings')}</p>
          <ul className='list-disc pl-5'>{warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
        </div>}
        {!impact.eligible && !operationId && <p role='status' className='text-amber-700'>
          {t(reasonKeys[impact.reason as keyof typeof reasonKeys] ?? reasonKeys.previewUnavailable)}
        </p>}
      </>}
      {mutation.error && <p role='alert' className='text-destructive'>{mutation.error.message}</p>}
      {operationId && <p role='status'>{t('landxmlMerge.cleanup.databaseDeleted')}</p>}
      {operation.data && <p role='status' className={operation.data.deletionOperation.status === 'FAILED' ? 'text-destructive' : ''}>
        {t(`landxmlMerge.cleanup.status.${operation.data.deletionOperation.status}`)}
        {' '}({operation.data.deletionOperation.completedTargets}/{operation.data.deletionOperation.totalTargets})
        {operation.data.deletionOperation.lastError && ` — ${operation.data.deletionOperation.lastError}`}
      </p>}
    </div>
    <DialogFooter>
      <Button variant='outline' onClick={dismissActiveDialog}>{t('landxmlMerge.cleanup.close')}</Button>
      {impact?.eligible && !operationId && <Button variant='destructive'
        disabled={preview.isFetching || mutation.isPending || !impact.fingerprint}
        onClick={() => mutation.mutate()}>{t('landxmlMerge.cleanup.deleteSources')}</Button>}
    </DialogFooter>
  </>;
}
